// sandboxStore — In-memory CRUD layer for sandbox impersonation.
// Mirrors scenarioStore.ts operations but never touches the database.
// All mutations live in memory and are discarded when impersonation ends.

import type { TaxScenario, TaxScenarioInput } from '@/tax/types';
import { calculateTaxServer } from '@/tax/taxApi';

export interface SandboxState {
  scenarios: TaxScenario[];
  drafts: TaxScenario[];
}

let _state: SandboxState = { scenarios: [], drafts: [] };
let _counter = 0;

function fakeId(): string {
  _counter += 1;
  return `sandbox-${Date.now()}-${_counter}`;
}

function fakeClientId(): string {
  return `sandbox-client-${Date.now()}-${++_counter}`;
}

function now(): string {
  return new Date().toISOString();
}

// ── Init / clear ─────────────────────────────────────────────────────

export function initSandbox(scenarios: TaxScenario[], drafts: TaxScenario[]): void {
  _state = {
    scenarios: scenarios.map(s => ({ ...s })),
    drafts: drafts.map(d => ({ ...d })),
  };
  _counter = 0;
}

export function clearSandbox(): void {
  _state = { scenarios: [], drafts: [] };
  _counter = 0;
}

export function getSandboxState(): SandboxState {
  return _state;
}

// ── Read ─────────────────────────────────────────────────────────────

export function sandboxLoadScenarios(): TaxScenario[] {
  return [..._state.scenarios].sort((a, b) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.createdAt.localeCompare(b.createdAt)
  );
}

export function sandboxLoadDrafts(): TaxScenario[] {
  return [..._state.drafts];
}

// Mirrors the DB's set_root_client_id trigger and rowToScenario's
// `root_client_id ?? client_id` fallback: a row added to an existing client
// inherits that client's lineage root; a brand-new client is its own root.
// Without this, sandbox-created clients had no rootClientId at all, so once
// duplicated they formed a folder keyed on undefined that could not be opened.
function lineageRootFor(clientId: string): string {
  const sibling = _state.scenarios.find(s => s.clientId === clientId)
    ?? _state.drafts.find(d => d.clientId === clientId);
  return sibling?.rootClientId ?? clientId;
}

// ── Create ───────────────────────────────────────────────────────────

export async function sandboxCreateScenario(
  input: TaxScenarioInput,
  _userId: string,
  clientId?: string,
): Promise<TaxScenario> {
  // Tax calculation is a read-only edge function call — safe in sandbox
  const results = await calculateTaxServer(input);
  const resolvedClientId = clientId ?? fakeClientId();
  const maxOrder = _state.scenarios
    .filter(s => s.clientId === resolvedClientId)
    .reduce((max, s) => Math.max(max, s.sortOrder ?? 0), -1);
  const scenario: TaxScenario = {
    id: fakeId(),
    clientId: resolvedClientId,
    rootClientId: lineageRootFor(resolvedClientId),
    input,
    results,
    createdAt: now(),
    updatedAt: now(),
    isDraft: false,
    sortOrder: maxOrder + 1,
  };
  _state.scenarios = [scenario, ..._state.scenarios];
  return scenario;
}

export async function sandboxSaveDraft(
  input: TaxScenarioInput,
  _userId: string,
  clientId?: string,
): Promise<TaxScenario> {
  const resolvedClientId = clientId ?? fakeClientId();
  const draft: TaxScenario = {
    id: fakeId(),
    clientId: resolvedClientId,
    rootClientId: lineageRootFor(resolvedClientId),
    input,
    results: { totalTaxLiability: 0, effectiveTaxRate: 0, adjustedGrossIncome: 0 } as any,
    createdAt: now(),
    updatedAt: now(),
    isDraft: true,
  };
  _state.drafts = [draft, ..._state.drafts];
  return draft;
}

// ── Update ───────────────────────────────────────────────────────────

export async function sandboxUpdateScenario(
  id: string,
  input: TaxScenarioInput,
): Promise<TaxScenario> {
  const results = await calculateTaxServer(input);
  const idx = _state.scenarios.findIndex(s => s.id === id);
  if (idx === -1) throw new Error('Scenario not found in sandbox');
  const updated: TaxScenario = {
    ..._state.scenarios[idx],
    input,
    results,
    updatedAt: now(),
  };
  _state.scenarios = _state.scenarios.map(s => (s.id === id ? updated : s));
  return updated;
}

export async function sandboxUpdateDraft(
  id: string,
  input: TaxScenarioInput,
): Promise<TaxScenario> {
  const idx = _state.drafts.findIndex(d => d.id === id);
  if (idx === -1) throw new Error('Draft not found in sandbox');
  const updated: TaxScenario = {
    ..._state.drafts[idx],
    input,
    updatedAt: now(),
    isDraft: true,
  };
  _state.drafts = _state.drafts.map(d => (d.id === id ? updated : d));
  return updated;
}

export async function sandboxFinalizeDraft(
  id: string,
  input: TaxScenarioInput,
): Promise<TaxScenario> {
  const results = await calculateTaxServer(input);
  const draft = _state.drafts.find(d => d.id === id);
  if (!draft) throw new Error('Draft not found in sandbox');
  const finalized: TaxScenario = {
    ...draft,
    input,
    results,
    updatedAt: now(),
    isDraft: false,
  };
  _state.drafts = _state.drafts.filter(d => d.id !== id);
  _state.scenarios = [finalized, ..._state.scenarios];
  return finalized;
}

// ── Delete ───────────────────────────────────────────────────────────

export function sandboxDeleteScenario(id: string): void {
  _state.scenarios = _state.scenarios.filter(s => s.id !== id);
  _state.drafts = _state.drafts.filter(d => d.id !== id);
}

// ── Duplicate ────────────────────────────────────────────────────────

export async function sandboxDuplicateScenario(
  scenario: TaxScenario,
  userId: string,
): Promise<TaxScenario> {
  return sandboxCreateScenario(
    { ...scenario.input, name: `${scenario.input.name} (Copy)` },
    userId,
    scenario.clientId,
  );
}

// ── Reorder ─────────────────────────────────────────────────────────

export function sandboxReorderScenarios(
  scenarioIds: string[],
  sortOrders: number[],
): void {
  const orderMap = new Map(scenarioIds.map((id, i) => [id, sortOrders[i]]));
  _state.scenarios = _state.scenarios.map(s =>
    orderMap.has(s.id) ? { ...s, sortOrder: orderMap.get(s.id)! } : s
  );
}
